import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Shield, Code, BarChart3, Bell, Plug, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../shared/ui';

/**
 * Landing Page - Public marketing page
 * Theme: Pure Black & White with Asymmetric Corners
 */
export function LandingPage() {
  const { t } = useTranslation();

  // Smooth scroll to section
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.scroll-animate').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b border-border bg-background">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-8">
          <div className="flex items-center gap-3">
            <img src="/android-chrome-192x192.png" alt="DevLint Logo" className="h-10 w-10" />
            <span className="text-xl font-bold text-text-primary font-mono">DevLint</span>
          </div>
          <div className="hidden items-center gap-8 md:flex">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="text-text-muted hover:text-text-primary transition-colors font-mono text-sm cursor-pointer">Features</a>
            <a href="#stats" onClick={(e) => scrollToSection(e, 'stats')} className="text-text-muted hover:text-text-primary transition-colors font-mono text-sm cursor-pointer">Stats</a>
            <a href="#cta" onClick={(e) => scrollToSection(e, 'cta')} className="text-text-muted hover:text-text-primary transition-colors font-mono text-sm cursor-pointer">Get Started</a>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-text-muted hover:text-text-primary transition-colors font-mono text-sm">{t('landing.nav.login')}</Link>
            <Link to="/login">
              <Button variant="primary" size="sm" corners="asymmetric">
                {t('landing.nav.getStarted')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen pt-24 pb-16">
        <div className="mx-auto max-w-5xl px-8">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img src="/android-chrome-512x512.png" alt="DevLint Logo" className="h-32 w-32 md:h-40 md:w-40" />
          </div>

          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 rounded-asymmetric-sm border border-border bg-surface px-4 py-2 text-sm text-text-secondary font-mono">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              {t('landing.hero.badge')}
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-center text-5xl font-bold leading-tight text-text-primary md:text-7xl font-mono tracking-tight">
            {t('landing.hero.title')}
            <br />
            <span className="text-text-secondary">
              {t('landing.hero.subtitle')}
            </span>
          </h1>

          {/* Subheadline */}
          <div style={{
            marginTop: '2rem',
            textAlign: 'center',
            fontSize: '1.5rem',
            color: '#888888',
            lineHeight: '1.8',
            maxWidth: '54rem',
            marginLeft: 'auto',
            marginRight: 'auto',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {t('landing.hero.description')}
          </div>

          {/* CTA Buttons */}
          <div className="mt-12 flex justify-center">
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <Link to="/login">
                <Button variant="primary" size="lg" corners="asymmetric" icon={<ArrowRight className="h-5 w-5" />} iconPosition="right">
                  {t('landing.hero.cta')}
                </Button>
              </Link>
              <Button variant="outline" size="lg" corners="asymmetric-inverse">
                {t('landing.hero.demo')}
              </Button>
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-text-muted font-mono">
            {t('landing.hero.noCard')}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-background scroll-mt-20">
        <div className="mx-auto max-w-7xl px-8">
          <div className="text-center scroll-animate">
            <h2 className="text-3xl font-bold text-text-primary md:text-4xl font-mono tracking-tight">
              {t('landing.features.title')}
            </h2>
            <p className="mt-4 text-lg text-text-muted">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3 scroll-animate scroll-animate-delay-2">
            <FeatureCard
              icon={<Shield className="h-6 w-6" />}
              title={t('landing.features.sast.title')}
              description={t('landing.features.sast.description')}
            />
            <FeatureCard
              icon={<Code className="h-6 w-6" />}
              title={t('landing.features.ai.title')}
              description={t('landing.features.ai.description')}
              inverse
            />
            <FeatureCard
              icon={<BarChart3 className="h-6 w-6" />}
              title={t('landing.features.debt.title')}
              description={t('landing.features.debt.description')}
            />
            <FeatureCard
              icon={<Bell className="h-6 w-6" />}
              title={t('landing.features.alerts.title')}
              description={t('landing.features.alerts.description')}
              inverse
            />
            <FeatureCard
              icon={<Plug className="h-6 w-6" />}
              title={t('landing.features.ide.title')}
              description={t('landing.features.ide.description')}
            />
            <FeatureCard
              icon={<BarChart3 className="h-6 w-6" />}
              title={t('landing.features.dashboard.title')}
              description={t('landing.features.dashboard.description')}
              inverse
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-16 border-y border-border scroll-mt-20">
        <div className="mx-auto max-w-7xl px-8">
          <div className="grid gap-8 text-center md:grid-cols-4 scroll-animate">
            <StatItem value="99.9%" label="Uptime SLA" />
            <StatItem value="<100ms" label="Analysis Speed" />
            <StatItem value="30+" label="Security Rules" />
            <StatItem value="4" label="Languages Supported" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-24 scroll-mt-20">
        <div className="mx-auto max-w-4xl px-8 text-center scroll-animate">
          <Card variant="highlighted" padding="lg" corners="asymmetric" className="border-primary">
            <h2 className="text-3xl font-bold text-text-primary md:text-4xl font-mono tracking-tight">
              {t('landing.cta.title')}
            </h2>
            <p className="mt-4 text-lg text-text-secondary">
              {t('landing.cta.subtitle')}
            </p>
            <Link to="/login" className="inline-block mt-8">
              <Button variant="primary" size="lg" corners="asymmetric">
                {t('landing.cta.button')}
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="mx-auto max-w-7xl px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-3">
              <img src="/favicon-32x32.png" alt="DevLint Logo" className="h-8 w-8" />
              <span className="font-medium text-text-primary font-mono">DevLint</span>
            </div>
            <p className="text-sm text-text-muted font-mono">
              {t('landing.footer.copyright')}
            </p>
            <div className="flex gap-8 text-sm text-text-muted font-mono">
              <Link to="/privacy" className="hover:text-text-primary transition-colors">{t('landing.footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-text-primary transition-colors">{t('landing.footer.terms')}</Link>
              <Link to="/contact" className="hover:text-text-primary transition-colors">{t('landing.footer.contact')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description, inverse = false }: { icon: React.ReactNode; title: string; description: string; inverse?: boolean }) {
  return (
    <Card
      variant="hover"
      padding="lg"
      corners={inverse ? 'asymmetric-inverse' : 'asymmetric'}
      className="group"
    >
      <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center ${inverse ? 'rounded-asymmetric-inverse-md' : 'rounded-asymmetric-md'} bg-surface-hover text-text-primary border border-border group-hover:border-primary transition-colors`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-text-primary font-mono">{title}</h3>
      <p className="mt-2 text-text-muted">{description}</p>
    </Card>
  );
}

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-4xl font-bold text-text-primary font-mono">{value}</div>
      <div className="mt-2 text-text-muted font-mono text-sm">{label}</div>
    </div>
  );
}
